<template>
  <v-row>
    <v-col>
      <ReportHeading
        label="Баланс Оплат/Послуг за Пакетами"
        :loading="loading"
        class="no-print"
        @update-report="updateReport"
      >
        <template v-slot:action>
          <PrintModeBtn
            tooltip-text="Режим звіту"
            @click="printMode = !printMode"
          />
          <v-switch
            v-model="withoutBonuses"
            :disabled="loading"
            color="green"
            label="Без бонусів"
            dense
            class="mt-1 mr-2"
            @change="updateReport"
          />
        </template>
        <template v-slot:filters>
          <BalanceFilter
            v-model="filter"
            :loading="loading"
          />
        </template>
      </ReportHeading>

      <v-row v-if="printMode">
        <BalancePrintForm
          v-if="printMode"
          :items="reports"
          :filter="filter"
          :total-start-month-balance="totalStartMonthBalance"
          :total-payed-hours="totalPayedHours"
          :total-spent-hours="totalSpentHours"
          :total-bonus-hours="totalBonusHours"
          :total-end-month-balance="totalEndMonthBalance"
          :total-services-hours="totalServicesHours"
        />
      </v-row>

      <v-data-table
        v-else
        :headers="tableHeaders"
        :items="reports"
        hide-default-footer
        hide-default-header
        :loading="loading"
        :items-per-page="reports.length"
        :height="reports.length? '57vh' : 'auto'"
        fixed-header
        class="elevation-1 mt-2"
      >
        <template v-slot:header>
          <thead>
            <tr>
              <th
                id="month"
                class="text-left"
              >
                {{ tableHeaders[0].text }}
              </th>
              <th
                id="startMonthBalance"
                class="text-right"
              >
                {{ tableHeaders[1].text }}
              </th>
              <th
                id="payedHours"
                class="text-right"
              >
                {{ tableHeaders[2].text }}
              </th>
              <th
                id="spentHours"
                class="text-right"
              >
                {{ tableHeaders[3].text }}
              </th>
              <th
                v-if="totalServicesHours > 0"
                id="servicesHours"
                class="text-right"
              >
                {{ tableHeaders[4].text }}
              </th>
              <th
                v-if="totalBonusHours !== 0"
                id="bonusHours"
                class="text-right"
              >
                {{ tableHeaders[5].text }}
              </th>
              <th
                id="endMonthBalance"
                class="text-right"
              >
                {{ tableHeaders[6].text }}
              </th>
            </tr>
          </thead>
        </template>
        <template v-slot:item="{ item }">
          <tr>
            <td class="text-capitalize">
              {{ item.month }}
            </td>
            <td class="text-right">
              <ReportNumber :value="item.startMonthBalance" />
            </td>
            <td class="text-right">
              <ReportNumber :value="item.payedHours" />
            </td>
            <td class="text-right">
              <ReportNumber :value="item.spentHours" />
            </td>
            <td
              v-if="totalServicesHours > 0"
              class="text-right"
            >
              <ReportNumber :value="item.servicesHours" />
            </td>
            <td
              v-if="totalBonusHours !== 0"
              class="text-right"
            >
              <ReportNumber :value="item.bonusHours" />
            </td>
            <td class="text-right">
              <ReportNumber :value="item.endMonthBalance" />
            </td>
          </tr>
        </template>

        <template
          v-if="reports.length"
          v-slot:footer
        >
          <v-layout>
            <CellFooter
              :is-report-number="false"
              cell-value="Разом:"
              :cell-width="monthCellWidth"
            />
            <CellFooter
              :is-report-number="true"
              :cell-value="totalPayedHours"
              :cell-width="payedHoursCellWidth"
            />
            <CellFooter
              :is-report-number="true"
              :cell-value="totalSpentHours"
              :cell-width="spentHoursCellWidth"
            />
            <CellFooter
              v-if="totalServicesHours > 0"
              :is-report-number="true"
              :cell-value="totalServicesHours"
              :cell-width="servicesHoursCellWidth"
            />
            <CellFooter
              v-if="totalBonusHours !== 0"
              :is-report-number="true"
              :cell-value="totalBonusHours"
              :cell-width="bonusHoursCellWidth"
            />
            <CellFooter
              :is-report-number="true"
              :cell-value="totalEndMonthBalance"
              :cell-width="endMonthBalanceCellWidth"
            />
          </v-layout>
        </template>
      </v-data-table>
    </v-col>
  </v-row>
</template>

<script>
import BalanceFilter from "./Filter.vue";
import ReportNumber from "@/components/ReportNumber.vue";
import BalancePrintForm from "./PrintForm.vue";
import headers from "./headers.js";
import * as reportsApi from "@/backend/reportsApi.js";
import ReportHeading from "../../../components/ReportHeading.vue";
import PrintModeBtn from "../../../components/PrintModeBtn.vue";
import CellFooter from "../../../components/cells/CellFooter.vue";

export default {
  components: {
    BalanceFilter,
    ReportNumber,
    BalancePrintForm,
    ReportHeading,
    PrintModeBtn,
    CellFooter,
  },
  data: () => ({
    loading: false,
    reports: [],
    filter: {
      dateFrom: "",
      dateTo: "",
      client: null,
    },
    printMode: false,
    monthCellWidth: "",
    payedHoursCellWidth: "",
    spentHoursCellWidth: "",
    servicesHoursCellWidth: "",
    bonusHoursCellWidth: "",
    endMonthBalanceCellWidth: "",
    withoutBonuses: false,
  }),
  computed: {
    tableHeaders() {
      return headers;
    },
    totalStartMonthBalance() {
      if (this.reports.length) {
        return this.reports[0].startMonthBalance;
      } else {
        return 0;
      }
    },
    totalPayedHours() {
      return this.reports.reduce((sum, current) => {
        return sum + current.payedHours;
      }, 0);
    },
    totalSpentHours() {
      return this.reports.reduce((sum, current) => {
        return sum + current.spentHours;
      }, 0);
    },
    totalBonusHours() {
      return this.reports.reduce((sum, current) => {
        return sum + current.bonusHours;
      }, 0);
    },
    totalServicesHours() {
      return this.reports.reduce((sum, current) => {
        return sum + current.servicesHours;
      }, 0);
    },
    totalEndMonthBalance() {
      return (
        Math.round(
          (this.totalStartMonthBalance +
            this.totalPayedHours -
            this.totalServicesHours +
            this.totalBonusHours -
            this.totalSpentHours) *
            100
        ) / 100
      );
    },
  },
  watch: {
    filter() {
      this.updateReport();
    },
  },
  updated() {
    this.monthCellWidth = document.getElementById("month")?.clientWidth;
    const startMonthBalanceCellWidth = document.getElementById("startMonthBalance")?.clientWidth;
    this.payedHoursCellWidth = document.getElementById("payedHours")?.clientWidth + startMonthBalanceCellWidth;
    this.spentHoursCellWidth = document.getElementById("spentHours")?.clientWidth;
    this.servicesHoursCellWidth = document.getElementById("servicesHours")?.clientWidth;
    this.bonusHoursCellWidth = document.getElementById("bonusHours")?.clientWidth;
    this.endMonthBalanceCellWidth = document.getElementById("endMonthBalance")?.clientWidth;
  },
  methods: {
    async updateReport() {
      this.loading = true;
      this.reports = [];
      const filterParams = {
        clientId: this.filter.client.id,
        dateFrom: this.filter.dateFrom,
        dateTo: this.filter.dateTo,
        withoutBonuses: this.withoutBonuses,
      };

      try {
        this.reports = await reportsApi
          .balanceUpdateReport(filterParams);
      } catch {
        this.$notifyWarning("В даних звіту помилки. Подивіться в Система/Стан БД");
      }
      this.loading = false;
    },
  },
};
</script>

<style>
.v-tabs__item {
  padding: 0 10px;
}
.v-tabs__container {
  height: 28px;
}
.v-tabs__div {
  text-transform: none;
}
</style>
